import { Component, EventEmitter, OnInit, Output, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnalysisSavedData, Mobile, TipoVia } from 'src/app/interfaces';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { Select } from '@interfaces';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { AuthService } from 'src/app/auth/auth.service';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Component({
  selector: 'app-business-name-pymes',
  templateUrl: './business-name-pymes.component.html',
  styleUrls: ['./business-name-pymes.component.scss']
})
export class BusinessNamePymesComponent implements OnInit, OnDestroy {
  @Output() formValid: EventEmitter<any> = new EventEmitter();
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public businessNameForm: FormGroup;
  private savedDataAnalysis: AnalysisSavedData = null;
  public roadTypeListRes = [];
  public roadTypeList: TipoVia[] = [];
  public defaultValueRoadType = '';
  public townList: Select[] = [];
  public defaultValueTown = '';
  private codigoProvincia: string;
  private dataLoaded = false;
  public selectPoblacionDisabled: boolean;
  public selectTipoViaDisabled: boolean;
  public isUserSpanish = true;
  public mobileCountryCodeList: Select[];

  constructor(
    private formBuilder: FormBuilder,
    private newContractService: NewContractPymesService,
    private utilService: UtilService,
    private utilValidatorsService: UtilValidatorsService,
    private authService: AuthService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.userData.data.country === 'ES';
    this.businessNameForm = this.formBuilder.group({
      businessName: [{ value: '', disabled: false }, [Validators.required, Validators.maxLength(70)]],
      cif: [{ value: '', disabled: true }],
      cnae: [
        { value: '', disabled: false },
        [Validators.required, Validators.maxLength(this.isUserSpanish ? 4 : 5), this.utilValidatorsService.validateCNAE]
      ],
      contactName: [{ value: '', disabled: false }, [Validators.required, Validators.maxLength(50)]],
      surname: [{ value: '', disabled: false }, [Validators.required, Validators.maxLength(50)]],
      dni: [{ value: '', disabled: false }, this.utilValidatorsService.validateDNI],
      email: [
        { value: '', disabled: false },
        [Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50)]
      ],
      mobileCountryCode: [{ value: '', disabled: false }, []],
      mobile: [
        { value: '', disabled: !this.isUserSpanish },
        [this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9)]
      ],
      roadType: [{ value: '', disabled: false }, Validators.required],
      roadName: [{ value: '', disabled: false }, [Validators.required, Validators.maxLength(100)]],
      addressNumber: [{ value: '', disabled: false }, [Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      gate: [{ value: '', disabled: false }, Validators.maxLength(10)],
      stair: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      floor: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      door: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      postalCode: [{ value: '', disabled: false }],
      postalCodePT1: [{ value: '', disabled: false }],
      postalCodePT2: [{ value: '', disabled: false }],
      town: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]],
      province: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]]
    });
    this.initMobileCountryCodeConfigPortugal();
    this.initPostalCodeconfigPortugal();

    this.businessNameForm.statusChanges.subscribe(result => {
      this.newContractService.setSavedData({ business: this.mapValues(this.businessNameForm) });
      if ((result === 'VALID' &&
        (this.isValidPostalCode() &&
          !!this.businessNameForm.controls.town.value && !!this.businessNameForm.controls.province.value
        )) || result === 'DISABLED'
      ) {
        this.formValid.emit({ value: true, tab: 1 });
      } else {
        this.formValid.emit({ value: false, tab: 1 });
      }
    });

    this.businessNameForm.controls.postalCode.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (this.businessNameForm.controls.postalCode.status !== 'DISABLED') {
        this.resetCodigoPostal();
        if (this.isValidPostalCode()) {
          this.newContractService.getCodigoPostal(this.businessNameForm.controls.postalCode.value)
            .pipe(takeUntil(this.destroyed$)).subscribe(res => {
              if (res.success) {
                if (res.data.codPostal != null) {
                  this.townList = [...this.mapTownListResponse(res.data.poblaciones)];
                  if (!this.dataLoaded) {
                    this.businessNameForm.controls.town.enable();
                  }
                  const town = this.townList.find(el =>
                    el.value === this.newContractService.getSavedData().value.business?.codigoPoblacionDireccion
                  );
                  this.defaultValueTown = town?.value || this.townList[0].value;
                  this.businessNameForm.patchValue({
                    town: this.defaultValueTown,
                    province: res.data.nombreProvincia
                  });
                  this.newContractService.setSavedData({ business: this.mapValues(this.businessNameForm) });
                }
              } else {
                this.utilService.toastErrors(res);
              }
            }, error => {
              this.utilService.toastErrors(error);
            });
        }
      }
    });

    this.businessNameForm.get('mobileCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      value => {
        if (value) {
          this.businessNameForm.get('mobile').enable();
          this.mobileCountryCodeChange(value);
        }
      }
    );

    this.newContractService.getTipoVia().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.roadTypeList = [...this.mapRoadTypeResponse(res?.data)];
      this.roadTypeListRes = [...this.mapRoadTypeResponse(res?.data)];
      this.newContractService
        .getSavedData()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(res2 => {
          if (res2 && !this.dataLoaded) {
            this.dataLoaded = true;
            this.businessNameForm.clearValidators();
            this.defaultValueRoadType = this.roadTypeListRes.find(el => el.label === res2.business?.nombreTipoViaDireccion)?.value;
            const mobileAndCountryCode: Mobile = this.getMobileAndCountryCode(res2.business?.telefonoContactoComercial);
            this.businessNameForm.patchValue({
              businessName: res2.business?.nombre,
              cif: res2.business?.cif || res2.analysis?.cif,
              cnae: res2.business?.cnae,
              contactName: res2.business?.nombreContactoComercial,
              surname: res2.business?.apellidosContactoComercial,
              dni: res2.business?.dniContactoComercial,
              email: res2.business?.mailContactoComercial,
              mobileCountryCode: mobileAndCountryCode.countryCode ? mobileAndCountryCode.countryCode : null,
              mobile: mobileAndCountryCode.mobile,
              roadType: this.defaultValueRoadType,
              roadName: res2.business?.nombreDireccion,
              addressNumber: res2.business?.numeroDireccion,
              stair: res2.business?.escaleraDireccion,
              floor: res2.business?.pisoDireccion,
              door: res2.business?.puertaDireccion,
              postalCode: res2.business?.codPostalDireccion,
              town: res2.business?.nombrePoblacionDireccion,
              gate: res2.business?.portalDireccion,
              province: res2.business?.nombreProvinciaDireccion
            });

            if (!this.isUserSpanish && res2.business) {
              this.businessNameForm.get('postalCodePT1').patchValue(res2.business?.codPostalDireccion?.slice(0, 4));
              this.businessNameForm.get('postalCodePT2').patchValue(res2.business?.codPostalDireccion?.slice(5, 8));
            }

            if (!res2.business?.editable) {
              this.selectTipoViaDisabled = true;
              this.selectPoblacionDisabled = true;
              this.businessNameForm.controls.businessName.disable();
              this.businessNameForm.controls.cif.disable();
              this.businessNameForm.controls.contactName.disable();
              this.businessNameForm.controls.surname.disable();
              this.businessNameForm.controls.dni.disable();
              this.businessNameForm.controls.email.disable();
              this.businessNameForm.controls.mobile.disable();
              this.businessNameForm.controls.mobileCountryCode.disable({ emitEvent: false });
              this.defaultValueRoadType = this.roadTypeListRes.find(el => el.label === res2.business?.nombreTipoViaDireccion)?.value;
              this.businessNameForm.controls.roadType.disable();
              this.businessNameForm.controls.roadName.disable();
              this.businessNameForm.controls.addressNumber.disable();
              this.businessNameForm.controls.gate.disable();
              this.businessNameForm.controls.stair.disable();
              this.businessNameForm.controls.floor.disable();
              this.businessNameForm.controls.door.disable();
              this.businessNameForm.controls.postalCode.disable();
              this.businessNameForm.controls.postalCodePT1.disable();
              this.businessNameForm.controls.postalCodePT2.disable();
              this.businessNameForm.controls.town.disable();
              this.businessNameForm.controls.gate.disable();
              this.businessNameForm.controls.province.disable();
            }
          } else if (res2?.analysis) {
            this.savedDataAnalysis = res2.analysis;
            if (this.businessNameForm.controls.cif.value === '') {
              this.businessNameForm.get('cif').patchValue(res2.analysis.cif);
              this.newContractService.setSavedData({ business: this.mapValues(this.businessNameForm) });
            }
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private mapRoadTypeResponse(values): TipoVia[] {
    const aux = [];
    if (values) {
      values.forEach(element => {
        aux.push({ label: element.nombreTipoVia, value: element.idTipoVia });
      });
    }
    return aux;
  }

  private mapValues(form): any {
    const savedBusinessData = this.newContractService.getSavedData().value.business;
    const formValues = form.controls;
    let nombreTipoVia = this.roadTypeListRes.find(el => el.value === formValues.roadType.value);
    nombreTipoVia = nombreTipoVia ? nombreTipoVia.label : '';
    let nombrePoblacion = null;
    let codigoPoblacion = null;

    if (formValues.town.value) {
      if (!this.selectPoblacionDisabled) {
        if (this.townList.length > 0) {
          nombrePoblacion = this.townList.find(el => el.value === formValues.town.value).label;
          codigoPoblacion = formValues.town.value;
        } else {
          nombrePoblacion = formValues.town.value;
          codigoPoblacion = savedBusinessData.codigoPoblacionDireccion || formValues.town.value;
        }
      } else {
        nombrePoblacion = savedBusinessData.nombrePoblacionDireccion;
        codigoPoblacion = savedBusinessData.codigoPoblacionDireccion;
      }
    } else if (savedBusinessData.nombrePoblacionDireccion) {
      nombrePoblacion = savedBusinessData.nombrePoblacionDireccion;
      codigoPoblacion = savedBusinessData.codigoPoblacionDireccion;
    }
    const contactoCRM = this.newContractService.getSavedData().value.contactoCRM;
    const idBorrador = this.newContractService.getSavedData().value.idBorrador;
    const telefonoContactoComercialValue = this.isUserSpanish
      ? formValues.mobile.value
      : formValues.mobileCountryCode.value + formValues.mobile.value;
    return {
      contactoCRM,
      idBorrador,
      editable: this.newContractService.getSavedData().value.business?.editable,
      nombre: formValues.businessName.value,
      cif: this.businessNameForm.controls.cif.value || null,
      cnae: formValues.cnae.value || null,
      nombreContactoComercial: formValues.contactName.value || null,
      apellidosContactoComercial: formValues.surname.value || null,
      dniContactoComercial: formValues.dni.value || null,
      telefonoContactoComercial: telefonoContactoComercialValue || null,
      mailContactoComercial: formValues.email.value || null,
      nombreTipoViaDireccion: nombreTipoVia || null,
      nombreDireccion: formValues.roadName.value || null,
      numeroDireccion: formValues.addressNumber.value || null,
      portalDireccion: formValues.gate.value || null,
      escaleraDireccion: formValues.stair.value || null,
      pisoDireccion: formValues.floor.value || null,
      puertaDireccion: formValues.door.value || null,
      codPostalDireccion: formValues.postalCode.value || null,
      nombrePoblacionDireccion: nombrePoblacion || null,
      nombreProvinciaDireccion: formValues.province.value || null,
      codigoPoblacionDireccion: codigoPoblacion || nombrePoblacion,
      codigoProvinciaDireccion: this.codigoProvincia
    };
  }
  private mapTownListResponse(values): Select[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element.nombrePoblacion, value: element.codigoPoblacion });
    });
    return aux;
  }

  private resetCodigoPostal(): void {
    this.townList = [];
    this.businessNameForm.controls.town.disable();
    this.businessNameForm.patchValue({
      town: null,
      province: null
    });
  }

  public mobileCountryCodeChange(value: string): void {
    if (value === '0034') {
      this.businessNameForm.get('mobile').setValidators(
        [this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9)]
      );
    } else if (value === '00351') {
      this.businessNameForm.get('mobile').setValidators(
        [this.utilValidatorsService.validateMobilePT, Validators.required, Validators.maxLength(9)]
      );
    }
    this.businessNameForm.get('mobile').updateValueAndValidity({ onlySelf: true });
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpanish && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.mobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodeConfigPortugal(): void {
    if (!this.isUserSpanish) {
      this.businessNameForm.get('mobileCountryCode').setValidators(Validators.required);
      this.initMobileCountryCodeList();
    }
  }

  private initMobileCountryCodeList(): void {
    this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.mobileCountryCodeList = this.utilService.getListCountryMobile();
    });
  }

  private initPostalCodeconfigPortugal(): void {
    if (!this.isUserSpanish) {
      this.businessNameForm.get('postalCodePT1').setValidators([Validators.required, Validators.maxLength(4)]);
      this.businessNameForm.get('postalCodePT2').setValidators([Validators.required, Validators.maxLength(3)]);
      this.businessNameForm.get('postalCode').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);

      this.businessNameForm.get('postalCodePT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.businessNameForm.get('postalCode').setValue(value + '-' + this.businessNameForm.get('postalCodePT2').value);
      });
      this.businessNameForm.get('postalCodePT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.businessNameForm.get('postalCode').setValue(this.businessNameForm.get('postalCodePT1').value + '-' + value);
      });
    } else {
      this.businessNameForm.get('postalCode').setValidators([Validators.required]);
    }
  }

  private isValidPostalCode(): boolean {
    if (this.isUserSpanish) {
      return this.businessNameForm.controls.postalCode.value?.length > 4;
    } else {
      return this.businessNameForm.get('postalCodePT1').value?.length === 4 &&
        this.businessNameForm.get('postalCodePT2').value?.length === 3 &&
        this.businessNameForm.get('postalCode').value?.length === 8;
    }
  }
}
